import { Delete2Light, PinLight } from '@ha/icons';
import React from 'react';
import { useSelector } from 'react-redux';
import { makeStyles } from '@material-ui/core';
import { InputProps } from '@material-ui/core/Input';
import { FieldRenderProps } from 'react-final-form';

import { IconButton, InputAdornment, units } from '@ha/dsl';
import { getInitialItems } from 'ha/modules/SavedSearches/selectors';
import Locations, { MIN_SEARCH_INPUT_LENGTH } from 'ha/modules/Locations';
import { PredictionType } from 'ha/types/GoogleMaps';

import SearchBarTextField from './SearchBarTextField';

interface Props {
  id: string;
  label?: string;
  dataTestLocator?: string;
  disabled?: boolean;
  predictionTypes: PredictionType[];
  name: string;
  required: boolean;
  placeholder?: string;
}

const useStyles = makeStyles(theme => ({
  icon: {
    marginLeft: units.rem(1),
    marginRight: units.rem(-0.5),
  },
  input: {
    fontWeight: 'bold',
    [theme.breakpoints.up('md')]: {
      fontSize: units.rem(2),
      lineHeight: 2,
      minHeight: '100%',
    },
  },
}));

export const LocationsInput: React.FC<FieldRenderProps<string> & Props> = ({
  input,
  meta,
  predictionTypes,
  label = '',
  id,
  dataTestLocator,
  disabled,
  name,
  placeholder,
  required,
}) => {
  const classes = useStyles();
  const initialItems = useSelector(getInitialItems);
  const [renderInitialItems, setRenderInitialItems] =
    React.useState<boolean>(false);

  const handleFocus = React.useCallback(
    (value: string) => {
      // Show initial suggestions on input focus
      if (input.value === '' || value === '') {
        setRenderInitialItems(true);
      }
    },
    [input],
  );

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      // User erased all symbols, lets show initial suggestions
      if (e.target.value.length === 0) {
        setRenderInitialItems(true);
      }

      // User typed MIN_SEARCH_INPUT_LENGTH letters
      // lets hide initial suggestions and show ones from google
      if (
        e.target.value.length >= MIN_SEARCH_INPUT_LENGTH &&
        renderInitialItems === true
      ) {
        setRenderInitialItems(false);
      }
    },
    [renderInitialItems],
  );

  const handleSelect = React.useCallback(() => {
    setRenderInitialItems(false);
  }, []);

  return (
    <Locations
      renderInput={inputProps => {
        // we extract the onBlur from InputProps because
        // if we pass down the event to the input directly
        // form validation does not work as expected
        // it will never render error
        // so we pass onBlur to TextField making validation work
        // and now we are able to select items with tab
        const { ref, onBlur, onFocus, onChange, ...other } = inputProps;

        return (
          <SearchBarTextField
            {...input}
            fullWidth
            name={name}
            InputProps={{
              inputRef: ref as React.RefObject<HTMLInputElement>,
              startAdornment: (
                <InputAdornment position="start" className={classes.icon}>
                  <PinLight />
                </InputAdornment>
              ),
              endAdornment: input.value && (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => {
                      input.onChange('');
                    }}
                  >
                    <Delete2Light />
                  </IconButton>
                </InputAdornment>
              ),
              ...(other as InputProps),
              classes: {
                input: classes.input,
              },
            }}
            onBlur={onBlur}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
              if (onChange) {
                handleChange(e);
                onChange(e);
              }
            }}
            onFocus={e => {
              if (onFocus) {
                handleFocus(input.value);
                onFocus(e as React.FocusEvent<HTMLInputElement>);
              }
            }}
            id={id}
            label={label}
            disabled={disabled}
            required={required}
            placeholder={placeholder}
            data-test-locator={dataTestLocator}
            error={meta.touched && meta.error}
            helperText={meta.touched && meta.error}
          />
        );
      }}
      value={input.value}
      predictionTypes={predictionTypes}
      onSelect={value => {
        handleSelect();
        input.onChange(value);
      }}
      onChange={input.onChange}
      renderInitialItems={renderInitialItems}
      initialItems={initialItems}
    />
  );
};

export default LocationsInput;
